import { useState } from "react";
import { useEditorStore } from "../stores/use-editor-store";
import { Region } from "@/api/studio/data/types/region.type";
import { toast } from "@/components/ui/toast";
import { useSegment } from "@/hooks/use-segment";
import { sceneColors } from "@/utils/scene-color-mapping";
import { CheckIcon } from "@heroicons/react/20/solid";
import { cn } from "@/utils/classes";

/* Prop Types */
interface SceneColorPickerModalProps {
  region: Region;
  onClose: () => void;
  projectId: string;
  studyId: string;
}

/*
 * SceneColorPickerModal component
 * This component is used to select a color for scene AOI classification
 */
export const SceneColorPickerModal = ({ region, onClose, projectId, studyId }: SceneColorPickerModalProps) => {
  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* State */
  const [selectedColor, setSelectedColor] = useState<string>(region.color || sceneColors[0].hex);

  /* Actions */
  const { updateRegionColor } = useEditorStore((state) => state.editorActions);

  /* Handlers */
  const handleColorSelect = (hex: string) => {
    setSelectedColor(hex);

    // Segment tracking
    segmentTrackEvent("AOI Classification Changed", {
      from: region.color,
      to: hex,
      projectId,
      studyId,
      studyType: "scene",
    });

    // Update the region color
    updateRegionColor(region.id, hex);

    // Toast
    toast({
      title: "Color Updated",
      type: "success",
    });

    onClose();
  };

  /* Render */
  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-sm font-semibold">Choose AOI colour:</h2>

      {/* Color Grid */}
      <div className="grid grid-cols-5 gap-3 items-center">
        {sceneColors.map((color) => {
          const isSelected = selectedColor === color.hex;

          return (
            <button
              key={color.hex}
              onClick={() => handleColorSelect(color.hex)}
              className={cn("relative size-5 rounded transition-all hover:scale-105 active:scale-95 cursor-pointer", {
                "ring-8 ring-base-300 ring-offset-0 ring-offset-base-100": isSelected,
              })}
              style={{ backgroundColor: color.hex }}
              title={color.name}
            >
              {isSelected && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <CheckIcon className="size-3 text-white drop-shadow-lg" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
